import React, { useState, useMemo } from "react";
import { Input } from "./ui/input";
import api, { formatEUR, formatPct, ASSET_ICONS } from "../lib/api";
import { ChevronDown, Search, TrendingUp, TrendingDown } from "lucide-react";
import ResponsiveDialog from "./ResponsiveDialog";

export default function AssetSelector({ value, onChange, prices = [] }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    if (!q) return prices;
    const k = q.toLowerCase();
    return prices.filter((p) => p.symbol.toLowerCase().includes(k) || p.name.toLowerCase().includes(k));
  }, [q, prices]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 bg-slate-50 hover:bg-slate-100 rounded-xl px-3 py-2 transition-colors border border-slate-200"
        data-testid="asset-selector-trigger"
      >
        <span
          className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
          style={{ backgroundColor: ASSET_ICONS[value] || "#0A3B2C" }}
        >{value}</span>
        <span className="font-display font-bold text-primary">{value}</span>
        <ChevronDown className="w-4 h-4 text-slate-500" />
      </button>
      <ResponsiveDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQ(""); }} title="Select asset" testId="asset-selector-dialog">
        <div className="pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search assets…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 rounded-xl"
              data-testid="asset-search-input"
              autoFocus
            />
          </div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto -mx-2">
          {filtered.map((p) => {
            const up = (p.change_24h || 0) >= 0;
            return (
              <button
                key={p.symbol}
                onClick={() => { onChange(p.symbol); setOpen(false); setQ(""); }}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-slate-50 transition-colors text-left"
                data-testid={`asset-option-${p.symbol}`}
              >
                <span
                  className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white"
                  style={{ backgroundColor: ASSET_ICONS[p.symbol] || "#0A3B2C" }}
                >{p.symbol.slice(0, 3)}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-primary truncate">{p.name}</div>
                  <div className="text-xs text-slate-500">{p.symbol}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-sm font-semibold text-primary tabular-nums">{formatEUR(p.price_eur)}</div>
                  <div className={`text-xs font-mono inline-flex items-center gap-0.5 ${up ? "xch-ticker-up" : "xch-ticker-down"}`}>
                    {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {formatPct(p.change_24h)}
                  </div>
                </div>
              </button>
            );
          })}
          {!filtered.length && (
            <div className="text-center py-10 text-sm text-slate-500">No assets found.</div>
          )}
        </div>
      </ResponsiveDialog>
    </>
  );
}
